import { useEffect, useState } from 'react';
import Api, { formatUrl } from '@plone/volto/helpers/Api/Api';

/**
 * Configuração dos tipos verificados de acessibilidade.
 *
 * O add-on v2inclusiva.a11y decide no painel (`@@a11y-controlpanel`) quais
 * tipos de conteúdo têm verificação. O Volto lê `GET /@a11y-config` em vez de
 * manter a lista fixa — assim o frontend acompanha a configuração.
 *
 * Cada item: `{ id, title, governado, por_setor }`. `governado` é true para os
 * tipos com fluxo completo (notícias): prazo, bloqueio de publicação e
 * despublicação. Os demais apenas verificam.
 *
 * O resultado é cacheado por sessão (módulo). Use `clearA11yConfigCache()`
 * depois de salvar o painel, se necessário.
 */

let cache = null; // { tipos: [...] } quando carregado
let emAndamento = null; // Promise em voo

export function clearA11yConfigCache() {
  cache = null;
  emAndamento = null;
}

export function fetchA11yConfig() {
  if (cache) return Promise.resolve(cache);
  if (!emAndamento) {
    emAndamento = new Api()
      .get(`${formatUrl('/')}@a11y-config`)
      .then((res) => {
        cache = { tipos: Array.isArray(res?.tipos) ? res.tipos : [] };
        return cache;
      })
      .catch(() => {
        emAndamento = null;
        return { tipos: [] };
      });
  }
  return emAndamento;
}

/**
 * Lista de tipos configurados quando já carregada, senão `null`.
 * Útil em funções fora do React (ex.: toast pós-salvar) sem bloquear.
 */
export function tiposConfigurados() {
  return cache ? cache.tipos : null;
}

export function useA11yConfig() {
  const [config, setConfig] = useState(cache);

  useEffect(() => {
    if (cache) return undefined;
    let ativo = true;
    fetchA11yConfig().then((c) => {
      if (ativo) setConfig(c);
    });
    return () => {
      ativo = false;
    };
  }, []);

  const tipos = config?.tipos || [];
  const porId = (id) => tipos.find((t) => t.id === id) || null;
  return {
    tipos,
    porId,
    loaded: Boolean(config),
    temTipo: (id) => tipos.some((t) => t.id === id),
  };
}
